import {
  AsYouType,
  getCountryCallingCode,
  isSupportedCountry,
  parsePhoneNumberFromString,
  type CountryCode as DialableCountryCode,
} from 'libphonenumber-js/max';
import { z } from 'zod';

import { type AuthErrorCode } from '../../auth/errors/auth-error-code';
import { type CountryCode } from '../../questions/country-code/country-code';

/**
 * A phone number as the platform stores it: **E.164** — `+`, the country
 * code, the national number, digits only, at most fifteen of them.
 *
 * This schema is the *storage* form and nothing more. Whether a number is a
 * real, reachable **mobile** in a country the deployment allows is decided by
 * `parseMobileNumber` below, on both the api and the clients; the api
 * re-validates, the client's check is UX only (P30 decision 6).
 */
export const PhoneNumberSchema = z.string().regex(/^\+[1-9]\d{6,14}$/);

export type PhoneNumber = z.infer<typeof PhoneNumberSchema>;

/** The two refusals a parse alone can make; the country policy is the api's. */
export type PhoneRefusal = Extract<AuthErrorCode, 'phone_invalid' | 'phone_not_mobile'>;

export type ParsedMobileNumber =
  { e164: PhoneNumber; refusal?: never } | { refusal: PhoneRefusal; e164?: never };

// `/max`, not `/mobile`: the mobile metadata answers `isValid() === false` for a
// landline with no type at all, so "not a mobile" and "not a number" would be one
// refusal — and the member's next action differs (another line vs a typo).
const MOBILE_TYPES: ReadonlySet<string> = new Set(['MOBILE', 'FIXED_LINE_OR_MOBILE']);

/**
 * Parses what a member typed for a country into the stored E.164, or says why not.
 *
 * The national input may carry a leading `+` and a full international number;
 * it is accepted only when it belongs to the chosen country's calling code —
 * the policy check upstream is made on `country`, so a number from elsewhere
 * behind an allowed flag would route around it. Calling codes rather than
 * `number.country`, because shared codes resolve to a sibling region
 * (`07911…` under GB parses as GG).
 */
export function parseMobileNumber(
  nationalNumber: string,
  country: CountryCode,
): ParsedMobileNumber {
  if (!isSupportedCountry(country)) {
    return { refusal: 'phone_invalid' };
  }

  const dialable = country as DialableCountryCode;
  const parsed = parsePhoneNumberFromString(nationalNumber, dialable);

  if (
    parsed === undefined ||
    !parsed.isValid() ||
    parsed.countryCallingCode !== getCountryCallingCode(dialable)
  ) {
    return { refusal: 'phone_invalid' };
  }

  const type = parsed.getType();
  if (type === undefined || !MOBILE_TYPES.has(type)) {
    return { refusal: 'phone_not_mobile' };
  }

  return { e164: parsed.number };
}

/** The stored E.164 in the country's own national format, for a screen that shows it. */
export function formatNational(e164: PhoneNumber): string {
  return parsePhoneNumberFromString(e164)?.formatNational() ?? e164;
}

/** `+966` for `SA`; `null` for a territory with no calling code of its own (AQ, BV, …). */
export function dialCodeOf(country: CountryCode): string | null {
  return isSupportedCountry(country)
    ? `+${getCountryCallingCode(country as DialableCountryCode)}`
    : null;
}

/**
 * The national grouping a member sees while they are still typing —
 * `AsYouType`'s own formatter, re-run on every keystroke rather than only on
 * the finished value, so a Saudi number reads `50 123 4567` as it is entered
 * instead of only once it parses.
 *
 * An unsupported country falls back to the raw digits: `AsYouType` throws for
 * a code it does not recognise, and a field mid-edit must never blank itself.
 */
export function formatNationalAsTyped(input: string, country: CountryCode): string {
  if (!isSupportedCountry(country)) {
    return input;
  }

  return new AsYouType(country as DialableCountryCode).input(input);
}
